import * as Sentry from '@sentry/react';
import axios, { AxiosHeaders } from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { AUTH_SESSION_FLAG } from '../contexts/AuthContext';
import type {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  User,
  ActiveWorksheetResponse,
  SaveProgressRequest,
  SaveProgressResponse,
  ProgressSummary,
  WorksheetAttempt,
  StudentLoginData,
  AddStudentData,
  StudentProfile,
  SubscriptionInfo,
  BillingActions,
} from '../types';

type RateLimitInfo = {
  limit: number | null;
  remaining: number | null;
  retryAfterSeconds: number | null;
};

class ApiService {
  private api: AxiosInstance;
  private lastRateLimitInfo: RateLimitInfo | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      withCredentials: true,
    });

    this.api.interceptors.request.use((config) => {
      const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
      if (match) {
        const token = decodeURIComponent(match[1]);
        // config.headers may be undefined or a plain object depending on the call
        // site, so normalize to an AxiosHeaders instance before setting the header.
        if (config.headers instanceof AxiosHeaders) {
          config.headers.set('X-XSRF-TOKEN', token);
        } else {
          config.headers = new AxiosHeaders({
            ...(config.headers as Record<string, unknown>),
            'X-XSRF-TOKEN': token,
          });
        }
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => {
        this.captureRateLimitHeaders(response);
        return response;
      },
      async (error: unknown) => {
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as {
            response?: {
              status?: number;
              data?: { request_id?: string };
              config?: { url?: string; _csrfRetry?: boolean };
              headers?: Record<string, string | number | undefined>;
            };
            config?: { url?: string; _csrfRetry?: boolean };
          };
          if (axiosError.response) {
            this.captureRateLimitHeaders({
              headers: axiosError.response.headers || {},
            } as AxiosResponse);

            // Session expired or token invalid — clear local state and redirect to login
            if (axiosError.response.status === 401) {
              const url = axiosError.response.config?.url ?? '';
              const isAuthEndpoint = ['/login', '/logout', '/register', '/user'].some((p) =>
                url.endsWith(p)
              );
              if (!isAuthEndpoint) {
                localStorage.removeItem(AUTH_SESSION_FLAG);
                window.location.href = '/login';
              }
            }

            // CSRF token expired — refresh and retry once
            if (axiosError.response.status === 419 && !axiosError.config?._csrfRetry) {
              await this.fetchCsrfCookie();
              const retryConfig = { ...axiosError.config, _csrfRetry: true };
              return this.api(retryConfig);
            }

            const requestId = axiosError.response.data?.request_id;
            if (requestId) {
              Sentry.withScope((scope) => {
                scope.setTag('backend_request_id', requestId);
                Sentry.captureException(error);
              });
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private captureRateLimitHeaders(response: AxiosResponse): void {
    const headers = (response.headers || {}) as Record<string, string | number | undefined>;
    const limitHeader = headers['x-ratelimit-limit'];
    const remainingHeader = headers['x-ratelimit-remaining'];
    const retryAfterHeader = headers['retry-after'];

    this.lastRateLimitInfo = {
      limit: limitHeader !== undefined ? Number(limitHeader) : null,
      remaining: remainingHeader !== undefined ? Number(remainingHeader) : null,
      retryAfterSeconds: retryAfterHeader !== undefined ? Number(retryAfterHeader) : null,
    };
  }

  private async fetchCsrfCookie(): Promise<void> {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    const origin = apiBase.startsWith('http') ? new URL(apiBase).origin : window.location.origin;
    await axios.get(`${origin}/sanctum/csrf-cookie`, { withCredentials: true });
  }

  getRateLimitInfo(): RateLimitInfo | null {
    return this.lastRateLimitInfo;
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    await this.fetchCsrfCookie();
    const response: AxiosResponse<AuthResponse> = await this.api.post('/login', credentials);
    return response.data;
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    await this.fetchCsrfCookie();
    const response: AxiosResponse<AuthResponse> = await this.api.post('/register', data);
    return response.data;
  }

  async resendVerificationEmail(): Promise<void> {
    await this.api.post('/email/verification-notification');
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  async getUser(): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get('/user');
    return response.data;
  }

  // Tiers
  async getTierData(): Promise<{
    current_tier: {
      id: number;
      name: string;
      display_name: string;
      price_monthly: number;
      student_limit: number;
      features: string[];
    } | null;
    is_inherited: boolean;
    remaining_student_slots: number;
    features: string[];
    subscription: SubscriptionInfo | null;
    billing_actions: BillingActions | null;
  }> {
    const response: AxiosResponse<{
      current_tier: {
        id: number;
        name: string;
        display_name: string;
        price_monthly: number;
        student_limit: number;
        features: string[];
      } | null;
      is_inherited: boolean;
      remaining_student_slots: number;
      features: string[];
      subscription: SubscriptionInfo | null;
      billing_actions: BillingActions | null;
    }> = await this.api.get('/tiers/me');
    return response.data;
  }

  // Billing
  async createCheckoutSession(
    tierId: number,
    billingInterval: 'monthly' | 'annual'
  ): Promise<{ checkout_url: string }> {
    const response: AxiosResponse<{ checkout_url: string }> = await this.api.post(
      '/billing/checkout',
      { tier_id: tierId, billing_interval: billingInterval }
    );
    return response.data;
  }

  async cancelSubscription(): Promise<void> {
    await this.api.post('/billing/cancel');
  }

  async resumeSubscription(): Promise<void> {
    await this.api.post('/billing/resume');
  }

  async getBillingPortalUrl(): Promise<{ portal_url: string }> {
    const response: AxiosResponse<{ portal_url: string }> = await this.api.get('/billing/portal');
    return response.data;
  }

  // Active Worksheet
  async getActiveWorksheet(): Promise<ActiveWorksheetResponse> {
    const response: AxiosResponse<ActiveWorksheetResponse> = await this.api.get(
      '/worksheets/active'
    );
    return response.data;
  }

  // Save Progress
  async saveProgress(
    worksheetId: number,
    data: SaveProgressRequest
  ): Promise<SaveProgressResponse> {
    const response: AxiosResponse<SaveProgressResponse> = await this.api.patch(
      `/worksheets/${worksheetId}/save-progress`,
      data
    );
    return response.data;
  }

  // Progress Summary
  async getProgressSummary(): Promise<ProgressSummary> {
    const response: AxiosResponse<ProgressSummary> = await this.api.get('/progress/summary');
    return response.data;
  }

  // Worksheet History
  async getWorksheetHistory(): Promise<{ history: WorksheetAttempt[] }> {
    const response: AxiosResponse<{ history: WorksheetAttempt[] }> = await this.api.get(
      '/worksheets/history'
    );
    return response.data;
  }

  // Worksheet Detail
  async getWorksheetDetail(attemptId: number): Promise<{ attempt: WorksheetAttempt }> {
    const response: AxiosResponse<{ attempt: WorksheetAttempt }> = await this.api.get(
      `/worksheets/history/${attemptId}`
    );
    return response.data;
  }

  // Student Login
  async studentLogin(data: StudentLoginData): Promise<AuthResponse> {
    await this.fetchCsrfCookie();
    const response: AxiosResponse<AuthResponse> = await this.api.post('/student-login', data);
    return response.data;
  }

  // Parent API methods
  async addStudent(data: AddStudentData): Promise<{ student: StudentProfile }> {
    const response: AxiosResponse<{ student: StudentProfile }> = await this.api.post(
      '/parent/students',
      data
    );
    return response.data;
  }

  async getStudents(): Promise<{ students: StudentProfile[] }> {
    const response: AxiosResponse<{ students: StudentProfile[] }> = await this.api.get(
      '/parent/students'
    );
    return response.data;
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
